import React, { useState, useEffect } from 'react';
import { IconButton, Badge, Tooltip, CircularProgress, useTheme } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { useUser } from './userContext';
import { useThemeContext, THEME_CLASSIC, THEME_DARK } from '../theme/ThemeContext';
import { NOTIFICATION_READ_EVENT } from '../pages/NotificationsPage';

/**
 * NotificationsButton - Button mit Benachrichtigungs-Icon, der die Anzahl ungelesener Nachrichten anzeigt
 * und zur Benachrichtigungsseite navigiert
 */
export const NotificationsButton = ({
    position = 'inline',  // inline, topRight, topLeft, bottomRight, bottomLeft
    size = 'medium',      // small, medium, large
    inHeader = false,     // true wenn im Header des Dialogs
    ...props
}) => {
    const theme = useTheme();
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { user } = useUser();
    const navigate = useNavigate();
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isDark = currentTheme === THEME_DARK;

    // Positions-Stile für unterschiedliche Platzierungen
    const positionStyles = {
        topRight: { position: 'absolute', top: 16, right: 16 },
        topLeft: { position: 'absolute', top: 16, left: 16 },
        bottomRight: { position: 'absolute', bottom: 16, right: 16 },
        bottomLeft: { position: 'absolute', bottom: 16, left: 16 },
        inline: { display: 'inline-flex', marginLeft: 8 }
    };

    // Icon-Größen-Konfiguration
    const iconSize = {
        small: { size: 'small', iconSize: 18 },
        medium: { size: 'medium', iconSize: 24 },
        large: { size: 'large', iconSize: 32 }
    };

    // Bedingte Hintergrundfarbe für verschiedene Themes und Positionen
    const hoverBackgroundColor = !isClassic && inHeader
        ? (isDark ? 'rgba(66, 165, 245, 0.15)' : theme.palette.info.lighter || '#e1f5fe')
        : !isClassic
            ? 'rgba(63, 81, 181, 0.1)'
            : 'transparent';

    const backgroundColor = !isClassic && inHeader
        ? (isDark ? 'rgba(66, 165, 245, 0.2)' : theme.palette.info.light)
        : !isClassic
            ? 'rgba(63, 81, 181, 0.2)'
            : theme.palette.action.hover;

    // Farbe des Badges abhängig von der Anzahl der ungelesenen Nachrichten
    const getBadgeColor = () => {
        if (unreadCount === 0) return 'default';
        if (unreadCount > 10) return 'error';
        if (unreadCount > 5) return 'warning';
        return 'primary';
    };

    // Ungelesene Nachrichten abrufen
    const fetchUnreadNotifications = async () => {
        if (!user || !user.api_key) return;

        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URI}/api/user-notifications/unread-count`, {
                method: 'GET',
                headers: {
                    'Authorization': user.api_key,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Fehler beim Abrufen der ungelesenen Benachrichtigungen');
            }

            const data = await response.json();
            setUnreadCount(data.data.count);
        } catch (err) {
            console.error('Fehler bei der Abfrage ungelesener Benachrichtigungen:', err);
            setError(err.message);
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Effekt zur Verarbeitung von Benachrichtigungsleseereignissen
    useEffect(() => {
        // Ereignishandler für das Benachrichtigungsleseereignis
        const handleNotificationReadUpdate = (event) => {
            // Aktualisiere den ungelesenen Zähler direkt mit dem neuen Wert
            if (event.detail && typeof event.detail.unreadCount === 'number') {
                setUnreadCount(event.detail.unreadCount);
            }
        };

        // Event-Listener für Benachrichtigungsleseereignisse registrieren
        window.addEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadUpdate);

        // Event-Listener beim Unmounten entfernen
        return () => {
            window.removeEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadUpdate);
        };
    }, []);

    // Beim Laden der Komponente und bei Änderungen des Users die ungelesenen Nachrichten abrufen
    useEffect(() => {
        if (user?.user_id) {
            fetchUnreadNotifications();

            // Polling alle 60 Sekunden, um aktuelle Benachrichtigungen zu erhalten
            const intervalId = setInterval(fetchUnreadNotifications, 60000);

            // Intervall beim Unmounten aufräumen
            return () => clearInterval(intervalId);
        }
        // eslint-disable-next-line
    }, [user]);

    // Handler für Klick auf den Button
    const handleClick = () => {
        navigate('/notifications');
    };

    return (
        <Tooltip title="Benachrichtigungen anzeigen">
            <IconButton
                color="primary"
                onClick={handleClick}
                size={iconSize[size].size}
                sx={{
                    ...positionStyles[position],
                    bgcolor: backgroundColor,
                    '&:hover': {
                        bgcolor: hoverBackgroundColor,
                    },
                    ...(inHeader && {
                        p: 0.75,
                        borderRadius: '50%',
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                    }),
                    ...props.sx
                }}
                {...props}
            >
                {isLoading ? (
                    <CircularProgress size={iconSize[size].iconSize * 0.8} color="inherit" />
                ) : (
                    <Badge
                        badgeContent={unreadCount}
                        color={getBadgeColor()}
                        overlap="circular"
                        max={99}
                    >
                        <NotificationsIcon sx={{ fontSize: iconSize[size].iconSize }} />
                    </Badge>
                )}
            </IconButton>
        </Tooltip>
    );
};

export default NotificationsButton;